import HistoryIcon from "@mui/icons-material/History";
import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { type MouseEvent } from "react";
import {
  proposalFieldHistorySurfaceEventGuards,
  useProposalFieldHistoryPopoverTrigger,
} from "work/proposals/negotiation/use-proposal-field-history-popover-trigger";
import { useSession } from "users/__session/session-context";
import ProposalCarriedOverFieldHistory from "work/proposals/negotiation/proposal-carried-over-field-history";
import { useProposalCarriedOverNegotiation } from "work/proposals/negotiation/proposal-carried-over-negotiation-context";
import { useMarkProposalChangesSeenMutation } from "work/proposals/negotiation/proposal-collaboration-api";
import {
  ProposalFieldChangeBadgeBubbles,
  useProposalFieldChangeBadgeState,
} from "work/proposals/negotiation/proposal-field-change-badge";
import {
  proposalTimelineItemPathForField,
  proposalTimelinePathForField,
} from "work/proposals/negotiation/proposal-field-timeline-path";
import ProposalTimelinePanel from "work/proposals/negotiation/proposal-timeline-panel";
import ProposalField from "work/proposals/proposal-fields/proposal-field";

const HistoryPanelContainer = styled(Box)(({ theme }) => ({
  maxHeight: 420,
  maxWidth: 440,
  minWidth: 300,
  overflowY: "auto",
  padding: theme.spacing(1.5),
}));

// Above the history it explains, so a reader sees why nothing in it responds before trying it.
const ReadOnlyReason = styled(Typography)(({ theme }) => ({
  display: "block",
  paddingBottom: theme.spacing(1),
})) as typeof Typography;

const HistoryIconContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "inline-flex",
  justifyContent: "center",
  position: "relative",
}));

type ProposalFieldHistoryButtonProps = {
  field: ProposalField;
  negotiationId?: string;
  viewerScope?: string;
  /**
   * Narrows the panel to one item of a collection field, by its identity in the negotiation stream —
   * see `ProposalTimelinePanelProps.collectionItemId`. A fee-schedule category row passes its category
   * id, so the row's history is that category's alone rather than the whole schedule's (#4951).
   */
  collectionItemId?: string;
  /** Defaults to the signed-in user; the badge never counts the viewer's own changes as unread. */
  currentUserId?: string;
  toolTip?: string;
  disabled?: boolean;
  /**
   * Suppresses this button's own change-badge bubbles (both the unread count and the collision "!") —
   * opening the popover still marks the field seen. Set by callers that already place a
   * {@link ProposalFieldChangeBadge} directly on the field's input — the active Details-tab
   * Name/Description/date fields, whose `TextRedlineInput`/`DateRedlinePicker` embed this button beside
   * their comment icon — so the same signal is never shown twice on one field; it just moves to the
   * input the button sits beside.
   */
  hideBadge?: boolean;
  /**
   * Why the viewer cannot put an earlier value back, stated at the top of the panel (#6874) — for
   * example "Read-only: you can't edit this proposal." This panel never restores anything; a field the
   * viewer may edit offers its revert through `ProposalFieldTimelineDropdown` instead. Omit it where the
   * reason is not known, and the panel says nothing.
   */
  readOnlyReason?: string;
};

/**
 * Inline per-field change history, in the field's own action row: the read-only ProposalTimelinePanel,
 * scoped to one field. This is where a proposal's change history is read — the whole-proposal History
 * drawer that repeated it was removed in #5567.
 *
 * On a revision the popover also shows the field's history from the negotiation the revision
 * continues, under "Before this revision" (see `ProposalCarriedOverFieldHistory`).
 *
 * Renders nothing until the negotiation stream exists (drafts before their first save, or the legacy
 * path). Keeping every Redux/session hook inside the inner component — mounted only once negotiationId
 * and viewerScope are present — lets the many call sites that render this without a store (and without a
 * negotiation) keep working untouched.
 *
 * The panel is a `Popper`, not a `Popover`, because it must never seize the page. The change traverser
 * can open this panel without a click (see `proposal-field-history-request-context.ts`), and a
 * `Popover` is a `Modal`: MUI's modal manager marks every sibling of the modal root `aria-hidden`, so
 * an unprompted stop took the whole proposal — the field being read, and the Prev/Next buttons that
 * opened it — out of the accessibility tree until someone dismissed it. That is what the persistent
 * comments drawer this stop replaced never did. A `Popper` with a `ClickAwayListener` dismisses the
 * same way without hiding anything, so Next stays pressable underneath and the field stays editable.
 */
export default function ProposalFieldHistoryButton(props: Readonly<ProposalFieldHistoryButtonProps>) {
  const { field, negotiationId, viewerScope, collectionItemId, currentUserId, disabled, hideBadge, readOnlyReason } =
    props;
  const toolTip = props.toolTip ?? "View change history";

  if (!negotiationId || !viewerScope) return null;

  return (
    <ConnectedProposalFieldHistoryButton
      field={field}
      negotiationId={negotiationId}
      viewerScope={viewerScope}
      collectionItemId={collectionItemId}
      currentUserId={currentUserId}
      toolTip={toolTip}
      disabled={disabled}
      hideBadge={hideBadge}
      readOnlyReason={readOnlyReason}
    />
  );
}

type ConnectedProps = Omit<ProposalFieldHistoryButtonProps, "negotiationId" | "viewerScope"> & {
  negotiationId: string;
  viewerScope: string;
  toolTip: string;
};

function ConnectedProposalFieldHistoryButton({
  field,
  negotiationId,
  viewerScope,
  collectionItemId,
  currentUserId: explicitCurrentUserId,
  toolTip,
  disabled,
  hideBadge,
  readOnlyReason,
}: Readonly<ConnectedProps>) {
  // Shared with the timeline dropdown's trigger: the guard against a composite field control's
  // ancestor click handler tearing the row down on the same click that opened the popover, and the
  // page's standing request for this field's history (the change traverser's stop).
  const { anchorEl, isOpen, open, close, triggerRef, surfaceRef } = useProposalFieldHistoryPopoverTrigger(field);

  const session = useSession();
  const currentUserId = explicitCurrentUserId ?? session.user?.id?.value;
  const [markProposalChangesSeen] = useMarkProposalChangesSeenMutation();

  const carriedOver = useProposalCarriedOverNegotiation();

  const badgeState = useProposalFieldChangeBadgeState(negotiationId, field, currentUserId);
  const { unreadCount, latestUnseenSequence, badgeLabel, hasPendingChange } = badgeState;

  function handleOpen(event: MouseEvent<HTMLElement>) {
    open(event);

    // Marking seen on open is the analog of markFieldCommentsAsRead firing when a comment thread opens:
    // advance the field's cursor past the changes just surfaced so the badge (and any collision on this
    // field the viewer has now acknowledged) clears. Nothing to clear when nothing is unread — skip the
    // network call.
    // A fee-schedule category acknowledges itself and not its siblings: what was surfaced is this
    // category's history, so the cursor that moves is the category's own (#5118).
    const path = proposalTimelineItemPathForField(field) ?? proposalTimelinePathForField(field);
    if (path && unreadCount > 0) {
      void markProposalChangesSeen({
        negotiationId,
        path,
        throughStreamVersion: latestUnseenSequence,
      });
    }
  }

  return (
    <>
      <Tooltip title={toolTip}>
        <span>
          <IconButton
            ref={triggerRef}
            size="medium"
            disabled={disabled}
            aria-label={badgeLabel ? `${toolTip} (${badgeLabel})` : toolTip}
            onClick={handleOpen}
          >
            <HistoryIconContainer>
              {!hideBadge && <ProposalFieldChangeBadgeBubbles state={badgeState} />}
              <HistoryIcon
                color={badgeState.isCollision ? "error" : hasPendingChange ? "info" : "inherit"}
                fontSize="medium"
              />
            </HistoryIconContainer>
          </IconButton>
        </span>
      </Tooltip>
      <Popper
        open={isOpen}
        anchorEl={anchorEl}
        placement="bottom-end"
        // `Popper` names its own root `role="tooltip"`, which this is not: the panel it carries states
        // its own `region`, and a tooltip wrapper around that only misreports it.
        role="presentation"
        // Above the page's own drawers and sticky headers; a `Popover` used to get this for free.
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        <ClickAwayListener onClickAway={close}>
          <Paper ref={surfaceRef} elevation={8} {...proposalFieldHistorySurfaceEventGuards}>
            <HistoryPanelContainer>
              {readOnlyReason && (
                <ReadOnlyReason variant="caption" color="text.secondary">
                  {readOnlyReason}
                </ReadOnlyReason>
              )}
              {/* Only fetch while the panel is open; comments stay in the dedicated comments surface. */}
              <ProposalTimelinePanel
                negotiationId={negotiationId}
                viewerScope={viewerScope}
                field={field}
                collectionItemId={collectionItemId}
                enabled={isOpen}
                includeComments={false}
                period={carriedOver ? "revision" : undefined}
              />
              <ProposalCarriedOverFieldHistory field={field} collectionItemId={collectionItemId} enabled={isOpen} />
            </HistoryPanelContainer>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}
